import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
} from "react";
import Card from "react-bootstrap/esm/Card";

import { useParams } from "react-router-dom";
import { StayFeedLoadingPlaceholder } from "../../../../Screens/DashboardView/MasterTemplateEditor/WebsiteLibFeedSkeleton";
import PostTemplate from "../CommonComponents/PostTemplate";
import { debounce } from "lodash";

import { useLoadTypeContentData } from "../../../../Utils/customHooks/UpToDateContent/useLoadTypeContent";
import { getContentFeedDateWindowFromComponent } from "../../../../Screens/DashboardView/StayUpToDate/AddComponentPicker/contentFeedTimeframe";
import {
  handleFirstImage,
  updateIsEmptyStatus,
} from "../../../../Utils/StayUpToDate/RenderComponent";
import { useDomLoaded } from "../../../../hooks/DomLoadedContext";

import "../../../../styles/StayUptoDateCSS/SubComponents.css";
import "./PodcastData.css";
import { FeedSectionEmptyState } from "../FeedSectionEmptyState";
import { websiteEditorFeedEmptyMessage } from "../../../../Screens/DashboardView/MasterTemplateEditor/websiteEditorFeedEmptyMessage";

const ITEMS_PER_PAGE = 3;

const PodcastData = memo(
  (props: any) => {
    const {
      user_id,
      username,
      title,
      handleRedirect,
      brand_color,
      component,
      removedPostIds,
      from,
      designTokens,
    templateGridLoaderProps,
      showEmptyInEditor = false,
    } = props;

    const platform = props.platform || "podcast";
    const type = props.type || "PODCAST";

    const param = useParams();
    const domLoaded = useDomLoaded();

    const contentDateWindow = useMemo(
      () => getContentFeedDateWindowFromComponent(component),
      [component?.details, component?.id]
    );

    const { isLoading: PodcastDataLoading, data: PodcastData }: any =
      useLoadTypeContentData(
        username,
        user_id,
        platform,
        type,
        removedPostIds,
        component?.content_order,
        component?.custom_order?.split(","),
        component?.shoppable_content_filter,
        component?.pinned_posts?.split(",") || [],
        component?.shoppable_status_filter || "all",
        "default",
        "default",
        true,
        contentDateWindow
      );

    const podcastItems = useMemo(() => {
      const pages = PodcastData?.pages;
      if (!Array.isArray(pages) || pages.length === 0) return [];

      return pages.reduce((acc: any[], page: any) => {
        if (Array.isArray(page?.data)) acc.push(...page.data);
        return acc;
      }, []);
    }, [PodcastData]);

    const totalCount = PodcastData?.pages?.[0]?.total_count ?? 0;

    const [currentIndex, setCurrentIndex] = useState(0);
    const containerRef = useRef<any>(null);
    const isButtonClicked = useRef(false);

    // Keep the latest items length accessible without rebuilding debounced fn
    const itemsLenRef = useRef(0);
    useEffect(() => {
      itemsLenRef.current = podcastItems.length;
    }, [podcastItems.length]);

    // Create debounced scroll handler once, cancel on unmount
    const debouncedOnScroll = useMemo(() => {
      return debounce(() => {
        if (isButtonClicked.current) return;

        const el = containerRef.current;
        const len = itemsLenRef.current;
        if (!el || !len) return;

        const scrollLeft = el.scrollLeft;
        const itemWidth = el.scrollWidth / len;
        const newCurrentIndex = Math.round(scrollLeft / itemWidth);
        setCurrentIndex(newCurrentIndex);
      }, 100);
    }, []);

    useEffect(() => {
      return () => {
        debouncedOnScroll.cancel();
      };
    }, [debouncedOnScroll]);

    // Empty-status + first-image logic (same behavior)
    useEffect(() => {
      if (PodcastDataLoading) return;

      const run = async () => {
        if (!PodcastData || totalCount === 0) {
          if (!showEmptyInEditor) {
            await updateIsEmptyStatus(title, user_id, component.id, from);
          }
        } else {
          await handleFirstImage(title, component?.id, podcastItems?.[0], user_id, from);
        }
      };

      run();
      // NOTE: keeping deps consistent with original behavior
    }, [PodcastDataLoading, PodcastData, totalCount, podcastItems, title, user_id, component, from]);

    // Scroll to current index when it changes (same behavior)
    useEffect(() => {
      const el = containerRef.current;
      if (!el || !podcastItems.length) return;

      const itemWidth = el.scrollWidth / podcastItems.length;
      el.scrollTo({
        left: itemWidth * currentIndex,
        behavior: "smooth",
      });
    }, [currentIndex, podcastItems.length]);

    // Attach scroll listener (stable debounced handler)
    useEffect(() => {
      const el = containerRef.current;
      if (!el) return;

      el.addEventListener("scroll", debouncedOnScroll);
      return () => {
        el.removeEventListener("scroll", debouncedOnScroll);
      };
    }, [debouncedOnScroll]);

    const handlePrevPage = useCallback(() => {
      isButtonClicked.current = true;
      setCurrentIndex((prevIndex) => Math.max(prevIndex - 1, 0));
      setTimeout(() => {
        isButtonClicked.current = false;
      }, 500);
    }, []);

    const handleNextPage = useCallback(() => {
      isButtonClicked.current = true;
      setCurrentIndex((prevIndex) =>
        Math.min(
          prevIndex + 1,
          Math.max(podcastItems.length - ITEMS_PER_PAGE, 0)
        )
      );
      setTimeout(() => {
        isButtonClicked.current = false;
      }, 500);
    }, [podcastItems.length]);

    return (
      <div className="reels-wrapper">
        {PodcastDataLoading || !domLoaded ? (<StayFeedLoadingPlaceholder
          designTokens={designTokens}
          from={from}
          component={component}
          templateGridLoaderProps={templateGridLoaderProps}
        />) : (
          <>
            {podcastItems.length > 0 && totalCount > 0 ? (
              <PostTemplate
                processedData={podcastItems}
                total_count={totalCount}
                component={component}
                user_id={user_id}
                username={username}
                handleRedirect={handleRedirect}
                brand_color={brand_color}
                title={title}
                from={from}
                type={type}
                itemsPerPage={ITEMS_PER_PAGE}
                platform={platform}
                designTokens={designTokens}
                pinnedPostIds={component?.pinned_posts?.split(",") || []}
              />
            ) : showEmptyInEditor ? (
              <FeedSectionEmptyState
              title={title}
              designTokens={designTokens}
              message={websiteEditorFeedEmptyMessage("platform", { platform: "Podcast" })}
            />
            ) : (
              ""
            )}
          </>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.user_id === nextProps.user_id &&
      prevProps.username === nextProps.username &&
      prevProps.platform === nextProps.platform &&
      prevProps.type === nextProps.type &&
      prevProps.removedPostIds === nextProps.removedPostIds &&
      prevProps.component?.content_order === nextProps.component?.content_order &&
      prevProps.designTokens === nextProps.designTokens
    );
  }
);

export default PodcastData;